import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Book, Zap, Users, UserCircle, Settings, Bell, Activity, HelpCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { HelpSearch } from '@/components/help/HelpSearch';
import { HelpArticle } from '@/components/help/HelpArticle';
import { getDocById } from '@/data/docs';

/**
 * Quick start card for role-specific guides
 */
function QuickStartCard({ icon: Icon, title, description, onClick }) {
  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-primary/20"
      onClick={onClick}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-100">
            <Icon className="h-5 w-5 text-amber-600" />
          </div>
          <CardTitle className="text-lg">{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <CardDescription>{description}</CardDescription>
      </CardContent>
    </Card>
  );
}

/**
 * HelpCenter - Main documentation page with search and article viewing
 *
 * Routes:
 * - /help - Shows search interface
 * - /help/:docId - Shows specific article
 */
export default function HelpCenter() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const [selectedArticle, setSelectedArticle] = useState(null);

  // Get current user info
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = user.role || 'team_leader';

  // Load article from URL param on mount
  useEffect(() => {
    if (docId) {
      const doc = getDocById(docId);
      if (doc) {
        setSelectedArticle(doc);
      }
    } else {
      setSelectedArticle(null);
    }
  }, [docId]);

  // Handle article selection
  const handleSelectArticle = (doc) => {
    setSelectedArticle(doc);
    navigate(`/help/${doc.id}`);
  };

  // Handle back to search
  const handleBack = () => {
    setSelectedArticle(null);
    navigate('/help');
  };

  // Get role-specific quick start config
  const getQuickStartConfig = () => {
    switch (userRole) {
      case 'admin':
        return {
          icon: Zap,
          title: 'Admin Quick Start',
          description: 'Configure rules, manage notifications, and monitor all system activity.',
          docId: 'quickstart-admin'
        };
      case 'manager':
        return {
          icon: Users,
          title: 'Manager Quick Start',
          description: 'Track team performance, verify commitments, and view analytics.',
          docId: 'quickstart-manager'
        };
      case 'team_leader':
        return {
          icon: UserCircle,
          title: 'Team Leader Quick Start',
          description: 'Enter pulses, manage commitments, and track your daily progress.',
          docId: 'quickstart-tl'
        };
      default:
        return null;
    }
  };

  const quickStartConfig = getQuickStartConfig();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <Book className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Help Center</h1>
          <p className="text-slate-600">
            Find answers to common questions and learn how to use all features
          </p>
        </div>
      </div>

      {/* Main content */}
      {selectedArticle ? (
        <HelpArticle doc={selectedArticle} onBack={handleBack} />
      ) : (
        <div className="space-y-6">
          {/* Search */}
          <HelpSearch
            onSelectArticle={handleSelectArticle}
            userRole={userRole}
            className="max-w-2xl"
          />

          {/* Quick start guide for current role */}
          {quickStartConfig && (
            <div className="pt-4">
              <h2 className="text-lg font-semibold text-slate-900 mb-3">
                Get Started
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <QuickStartCard
                  icon={quickStartConfig.icon}
                  title={quickStartConfig.title}
                  description={quickStartConfig.description}
                  onClick={() => {
                    const doc = getDocById(quickStartConfig.docId);
                    if (doc) handleSelectArticle(doc);
                  }}
                />
              </div>
            </div>
          )}

          {/* Browse by Topic - Admin only sees admin-specific topics */}
          {userRole === 'admin' && (
            <div className="pt-4">
              <h2 className="text-lg font-semibold text-slate-900 mb-3">
                Browse by Topic
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Card
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => {
                    const doc = getDocById('rules-overview');
                    if (doc) handleSelectArticle(doc);
                  }}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-purple-100">
                        <Settings className="h-5 w-5 text-purple-600" />
                      </div>
                      <CardTitle className="text-base">Rules</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>
                      Create and manage notification rules to automate alerts
                    </CardDescription>
                  </CardContent>
                </Card>
                <Card
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => {
                    const doc = getDocById('notifications-overview');
                    if (doc) handleSelectArticle(doc);
                  }}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-100">
                        <Bell className="h-5 w-5 text-blue-600" />
                      </div>
                      <CardTitle className="text-base">Notifications</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>
                      Understand scheduled jobs and delivery channels
                    </CardDescription>
                  </CardContent>
                </Card>
                <Card
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => {
                    const doc = getDocById('activity-logs-overview');
                    if (doc) handleSelectArticle(doc);
                  }}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-green-100">
                        <Activity className="h-5 w-5 text-green-600" />
                      </div>
                      <CardTitle className="text-base">Activity Logs</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>
                      Track and audit all system activity
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Popular Questions */}
          <div className="pt-4">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">
              <HelpCircle className="inline-block h-5 w-5 mr-2 text-slate-400" />
              Popular Questions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {userRole === 'admin' && (
                <>
                  <Card
                    className="cursor-pointer hover:shadow-md transition-shadow hover:border-purple-200"
                    onClick={() => {
                      const doc = getDocById('rules-overview');
                      if (doc) handleSelectArticle(doc);
                    }}
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base text-slate-700">How do I create a custom rule?</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription>
                        Learn to automate notifications with the rules engine
                      </CardDescription>
                    </CardContent>
                  </Card>
                  <Card
                    className="cursor-pointer hover:shadow-md transition-shadow hover:border-blue-200"
                    onClick={() => {
                      const doc = getDocById('notifications-overview');
                      if (doc) handleSelectArticle(doc);
                    }}
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base text-slate-700">What notifications are sent and when?</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription>
                        View scheduled jobs and delivery timing
                      </CardDescription>
                    </CardContent>
                  </Card>
                  <Card
                    className="cursor-pointer hover:shadow-md transition-shadow hover:border-green-200"
                    onClick={() => {
                      const doc = getDocById('activity-logs-overview');
                      if (doc) handleSelectArticle(doc);
                    }}
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base text-slate-700">How do I filter activity logs?</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription>
                        Search and filter the audit trail
                      </CardDescription>
                    </CardContent>
                  </Card>
                </>
              )}
              <Card
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => {
                  const doc = getDocById('commitment-guide');
                  if (doc) handleSelectArticle(doc);
                }}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-slate-700">How does verification work?</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>
                    Understand the commitment and verification workflow
                  </CardDescription>
                </CardContent>
              </Card>
              <Card
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => {
                  const doc = getDocById('dashboard-guide');
                  if (doc) handleSelectArticle(doc);
                }}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-slate-700">What do the dashboard metrics mean?</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>
                    Learn about KPIs and performance analytics
                  </CardDescription>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
